import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface Profile {
  id: string;
  name: string;
  slug: string;
  assetClass: string;
  isUniversal: boolean;
  isActive: boolean;
}

interface ProfileListProps {
  onSelectProfile?: (profileId: string) => void;
}

export function ProfileList({ onSelectProfile }: ProfileListProps) {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/asset-profiles");
      const data = await res.json();
      setProfiles(data.data ?? []);
    } catch {
      toast.error("Failed to load profiles");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  if (loading)
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );

  const grouped = profiles.reduce<Record<string, Profile[]>>((acc, p) => {
    (acc[p.assetClass] ??= []).push(p);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {Object.entries(grouped).map(([assetClass, profs]) => (
        <div key={assetClass}>
          <h3 className="text-lg font-semibold capitalize mb-3">{assetClass.replace("-", " ")}</h3>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[500px]">
              <thead>
                <tr className="border-b text-left text-sm text-muted-foreground">
                  <th className="pb-3 font-medium">Profile</th>
                  <th className="pb-3 font-medium">Type</th>
                  <th className="pb-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {profs.map((p) => (
                  <tr key={p.id} className="border-b">
                    <td className="py-3 font-medium">{p.name}</td>
                    <td className="py-3">{p.isUniversal && <Badge>Universal</Badge>}</td>
                    <td className="py-3">
                      {onSelectProfile && (
                        <Button variant="outline" size="sm" onClick={() => onSelectProfile(p.id)}>
                          Edit Weights
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
